import React, { useMemo, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SendIcon, MessageCircleIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getUTMDataForSubmission, appendUTMToUrl } from '../utils/utm';
const WORKER_URL = 'https://white-bonus-e94a.maxkhmlyt.workers.dev';
// Контактные данные
const TELEGRAM_LINK = 'https://t.me/+380508399309';
const WHATSAPP_LINK = 'https://api.whatsapp.com/send/?phone=380508399309&text&type=phone_number&app_absent=0';
// Country codes sorted by length (longest first) for proper matching
const COUNTRY_CODES = [{
  code: '+380',
  flag: '🇺🇦',
  country: 'Ukraine'
}, {
  code: '+375',
  flag: '🇧🇾',
  country: 'Belarus'
}, {
  code: '+372',
  flag: '🇪🇪',
  country: 'Estonia'
}, {
  code: '+371',
  flag: '🇱🇻',
  country: 'Latvia'
}, {
  code: '+370',
  flag: '🇱🇹',
  country: 'Lithuania'
}, {
  code: '+421',
  flag: '🇸🇰',
  country: 'Slovakia'
}, {
  code: '+420',
  flag: '🇨🇿',
  country: 'Czech'
}, {
  code: '+386',
  flag: '🇸🇮',
  country: 'Slovenia'
}, {
  code: '+385',
  flag: '🇭🇷',
  country: 'Croatia'
}, {
  code: '+381',
  flag: '🇷🇸',
  country: 'Serbia'
}, {
  code: '+359',
  flag: '🇧🇬',
  country: 'Bulgaria'
}, {
  code: '+358',
  flag: '🇫🇮',
  country: 'Finland'
}, {
  code: '+357',
  flag: '🇨🇾',
  country: 'Cyprus'
}, {
  code: '+356',
  flag: '🇲🇹',
  country: 'Malta'
}, {
  code: '+353',
  flag: '🇮🇪',
  country: 'Ireland'
}, {
  code: '+352',
  flag: '🇱🇺',
  country: 'Luxembourg'
}, {
  code: '+351',
  flag: '🇵🇹',
  country: 'Portugal'
}, {
  code: '+350',
  flag: '🇬🇮',
  country: 'Gibraltar'
}, {
  code: '+972',
  flag: '🇮🇱',
  country: 'Israel'
}, {
  code: '+971',
  flag: '🇦🇪',
  country: 'UAE'
}, {
  code: '+966',
  flag: '🇸🇦',
  country: 'Saudi Arabia'
}, {
  code: '+994',
  flag: '🇦🇿',
  country: 'Azerbaijan'
}, {
  code: '+995',
  flag: '🇬🇪',
  country: 'Georgia'
}, {
  code: '+998',
  flag: '🇺🇿',
  country: 'Uzbekistan'
}, {
  code: '+48',
  flag: '🇵🇱',
  country: 'Poland'
}, {
  code: '+49',
  flag: '🇩🇪',
  country: 'Germany'
}, {
  code: '+44',
  flag: '🇬🇧',
  country: 'UK'
}, {
  code: '+43',
  flag: '🇦🇹',
  country: 'Austria'
}, {
  code: '+41',
  flag: '🇨🇭',
  country: 'Switzerland'
}, {
  code: '+40',
  flag: '🇷🇴',
  country: 'Romania'
}, {
  code: '+39',
  flag: '🇮🇹',
  country: 'Italy'
}, {
  code: '+36',
  flag: '🇭🇺',
  country: 'Hungary'
}, {
  code: '+34',
  flag: '🇪🇸',
  country: 'Spain'
}, {
  code: '+33',
  flag: '🇫🇷',
  country: 'France'
}, {
  code: '+32',
  flag: '🇧🇪',
  country: 'Belgium'
}, {
  code: '+31',
  flag: '🇳🇱',
  country: 'Netherlands'
}, {
  code: '+30',
  flag: '🇬🇷',
  country: 'Greece'
}, {
  code: '+47',
  flag: '🇳🇴',
  country: 'Norway'
}, {
  code: '+46',
  flag: '🇸🇪',
  country: 'Sweden'
}, {
  code: '+45',
  flag: '🇩🇰',
  country: 'Denmark'
}, {
  code: '+90',
  flag: '🇹🇷',
  country: 'Turkey'
}, {
  code: '+1',
  flag: '🇺🇸',
  country: 'USA/Canada'
}, {
  code: '+7',
  flag: '🇷🇺',
  country: 'Russia/Kazakhstan'
}, {
  code: '+61',
  flag: '🇦🇺',
  country: 'Australia'
}, {
  code: '+81',
  flag: '🇯🇵',
  country: 'Japan'
}, {
  code: '+82',
  flag: '🇰🇷',
  country: 'South Korea'
}, {
  code: '+86',
  flag: '🇨🇳',
  country: 'China'
}, {
  code: '+91',
  flag: '🇮🇳',
  country: 'India'
}, {
  code: '+55',
  flag: '🇧🇷',
  country: 'Brazil'
}, {
  code: '+52',
  flag: '🇲🇽',
  country: 'Mexico'
}].sort((a, b) => b.code.length - a.code.length);
export function Consultation() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t,
    language,
    getPath
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const languageNames: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
    ua: 'Українська'
  };
  const consentText: Record<string, string> = {
    en: 'I agree to the processing of my personal data',
    ru: 'Я согласен на обработку моих персональных данных',
    ua: 'Я погоджуюсь на обробку моїх персональних даних',
    cz: 'Souhlasím se zpracováním osobních údajů'
  };
  const consentErrorText: Record<string, string> = {
    en: 'Please agree to continue',
    ru: 'Пожалуйста, дайте согласие для продолжения',
    ua: 'Будь ласка, дайте згоду для продовження'
  };
  const detectedCountry = useMemo(() => {
    const phone = formData.phone.replace(/\s/g, '');
    if (!phone.startsWith('+')) return null;
    for (const country of COUNTRY_CODES) {
      if (phone.startsWith(country.code)) {
        return country;
      }
    }
    return null;
  }, [formData.phone]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleConsentChange = () => {
    setConsent(!consent);
    if (!consent) {
      setConsentError(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }
    setIsSubmitting(true);
    const utmData = getUTMDataForSubmission();
    try {
      await fetch(WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message || 'Не указано',
          form_type: window.location.pathname.includes('instagram_ads') ? 'Instagram Ads' : window.location.pathname.includes('course') ? 'Курс (Footer)' : 'Консультация (Footer)',
          page_url: window.location.href,
          language: languageNames[language] || language,
          ...utmData
        })
      });
    } catch {
      // Ignore errors
    } // Fire Meta Pixel Lead event immediately before redirect
    try {
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Lead');
      }
    } catch (e) {}
    // Small delay to ensure the pixel request goes through before the page unloads
    setTimeout(() => {
      const thankYouPath = getPath('thank_you_page');
      // Используем pushState вместо location.href, чтобы React переключил страницу без перезагрузки (обходит ошибку 404 сервера)
      window.history.pushState({}, '', appendUTMToUrl(thankYouPath));
      window.dispatchEvent(new Event('popstate'));
      window.scrollTo(0, 0);
    }, 300);
  };
  return <section id="consultation" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-31">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden opacity-30" data-id="element-32">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
      }} data-id="element-33" />
      </div>

      <div className="relative z-10 mx-auto max-w-3xl" data-id="element-34">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-10 sm:mb-12 text-center" data-id="element-35">
          {/* H2 - Section heading: Get your free preview */}
          <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
          color: 'var(--text-primary)'
        }} data-id="element-36">
            {t('consultation.title')} {t('consultation.titleHighlight')}
          </h2>
          <p className="mx-auto max-w-xl text-sm sm:text-base" style={{
          color: 'var(--text-secondary)'
        }} data-id="element-37">
            {t('consultation.subtitle')}
          </p>
        </motion.div>

        {/* Form Card */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-38">
          <div className="rounded-3xl border p-6 sm:p-10 relative overflow-hidden" style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-secondary)'
        }} data-id="element-39">
            {/* Decorative corner accents */}
            <div className="absolute top-0 right-0 w-32 h-32 opacity-5" style={{
            background: 'radial-gradient(circle at top right, var(--accent), transparent 70%)'
          }} data-id="element-40" />

            <div className="absolute bottom-0 left-0 w-32 h-32 opacity-5" style={{
            background: 'radial-gradient(circle at bottom left, var(--accent), transparent 70%)'
          }} data-id="element-41" />

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 relative z-10" data-id="element-42">
              <motion.a href={TELEGRAM_LINK} target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="flex flex-1 items-center justify-center gap-3 rounded-xl py-3.5 font-semibold transition-all border text-sm sm:text-base" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-43">
                <SendIcon className="h-4 w-4 sm:h-5 sm:w-5" style={{
                color: 'var(--accent)'
              }} data-id="element-44" />

                {t('consultation.telegram')}
              </motion.a>

              <motion.a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="flex flex-1 items-center justify-center gap-3 rounded-xl py-3.5 font-semibold transition-all border text-sm sm:text-base" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)'
            }} data-id="element-45">
                <MessageCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" style={{
                color: 'var(--accent)'
              }} data-id="element-46" />

                {t('consultation.whatsapp')}
              </motion.a>
            </div>

            <div className="my-8 flex items-center gap-4 relative z-10" data-id="element-47">
              <div className="h-px flex-1" style={{
              backgroundColor: 'var(--border)'
            }} data-id="element-48" />

              <span className="text-xs sm:text-sm" style={{
              color: 'var(--text-muted)'
            }} data-id="element-49">
                {t('consultation.or')}
              </span>
              <div className="h-px flex-1" style={{
              backgroundColor: 'var(--border)'
            }} data-id="element-50" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 relative z-10" data-id="element-51">
              <div data-id="element-52">
                <label htmlFor="name" className="mb-2 block text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-53">
                  {t('consultation.name')} *
                </label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3.5 text-sm sm:text-base transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} placeholder={t('consultation.namePlaceholder')} data-id="element-54" />
              </div>

              <div className="grid sm:grid-cols-2 gap-5" data-id="element-55">
                <div data-id="element-56">
                  <label htmlFor="email" className="mb-2 block text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-57">
                    {t('consultation.email')} *
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3.5 text-sm sm:text-base transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('consultation.emailPlaceholder')} data-id="element-58" />
                </div>

                <div data-id="element-59">
                  <label htmlFor="phone" className="mb-2 block text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-60">
                    {language === 'ru' ? 'Телефон' : language === 'ua' ? 'Телефон' : 'Phone'}{' '}
                    *
                  </label>
                  <div className="relative" data-id="element-61">
                    {detectedCountry && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xl pointer-events-none" title={detectedCountry.country} data-id="element-62">
                        {detectedCountry.flag}
                      </div>}
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3.5 text-sm sm:text-base transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    paddingLeft: detectedCountry ? '2.75rem' : '1rem'
                  }} placeholder="+1 234 567 8900" data-id="element-63" />
                  </div>
                </div>
              </div>

              <div data-id="element-64">
                <label htmlFor="message" className="mb-2 block text-sm font-medium" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-65">
                  {t('consultation.desc')}
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} className="w-full resize-none rounded-xl border px-4 py-3.5 text-sm sm:text-base transition-all focus:outline-none focus:ring-2 focus:ring-neutral-500/30" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-tertiary)',
                color: 'var(--text-primary)'
              }} placeholder={t('consultation.descPlaceholder')} data-id="element-66" />
              </div>

              {/* Consent Checkbox */}
              <motion.div className={`flex items-start gap-3 p-3 rounded-xl transition-all ${consentError ? 'bg-red-500/10' : ''}`} animate={consentError ? {
              x: [0, -5, 5, -5, 5, 0]
            } : {}} transition={{
              duration: 0.4
            }} data-id="element-67">
                <button type="button" onClick={handleConsentChange} className="flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all" style={{
                borderColor: consentError ? '#ef4444' : consent ? 'var(--accent)' : 'var(--border)',
                backgroundColor: consent ? 'var(--accent)' : 'transparent'
              }} data-id="element-68">
                  {consent && <CheckIcon className="w-3 h-3 text-white" data-id="element-69" />}
                </button>
                <div className="flex-1" data-id="element-70">
                  <label className="text-sm cursor-pointer" style={{
                  color: consentError ? '#ef4444' : 'var(--text-secondary)'
                }} onClick={handleConsentChange} data-id="element-71">
                    {consentText[language] || consentText.en} *
                  </label>
                  {consentError && <motion.p initial={{
                  opacity: 0,
                  y: -5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-xs mt-1 font-medium" style={{
                  color: '#ef4444'
                }} data-id="element-72">
                      {consentErrorText[language] || consentErrorText.en}
                    </motion.p>}
                </div>
              </motion.div>

              <motion.button type="submit" disabled={isSubmitting} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className="group w-full rounded-xl py-4 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-70 text-sm sm:text-base" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-73">
                <span className="flex items-center justify-center gap-2" data-id="element-74">
                  {isSubmitting ? t('consultation.sending') : <>
                      {t('consultation.cta')}
                      <ArrowRightIcon className="h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-75" />
                    </>}
                </span>
              </motion.button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>;
}